import React, { useState, useEffect, useMemo } from 'react';
import { Order, OrderStatus, PaymentStatus } from '../types';
import { StatusBadge } from './StatusBadge';
import { 
  Edit2, Trash2, Eye, MapPin, Anchor, ChevronDown, ChevronUp, 
  Columns, Check, Package, RefreshCcw, ArrowUp, ArrowDown, ArrowUpDown 
} from 'lucide-react';

interface OrderTableProps {
  orders: Order[];
  onEdit?: (order: Order) => void;
  onDelete?: (id: string) => void;
  onView: (order: Order) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}

const STORAGE_KEY = 'nexus_order_pipeline_prefs_v13';

const COLUMN_IDS = [
  'date', 'ref', 'supplier', 'consignee', 'grade', 'quantity', 'rate', 'port', 'status'
] as const;

type ColumnId = typeof COLUMN_IDS[number];

const DEFAULT_VISIBILITY: Record<ColumnId, boolean> = {
  date: true,
  ref: true,
  supplier: true,
  consignee: true,
  grade: true,
  quantity: true,
  rate: true,
  port: true,
  status: true
};

const COLUMN_LABELS: Record<string, string> = {
  date: 'Date',
  ref: 'REF',
  supplier: 'Supplier',
  consignee: 'Consignee',
  grade: 'Grades',
  quantity: 'Total Qty',
  rate: 'Unit Rate',
  port: 'Destination Port',
  status: 'Status'
};

export const OrderTable: React.FC<OrderTableProps> = ({ orders, onEdit, onDelete, onView, canDelete = false, canEdit = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnId, boolean>>(DEFAULT_VISIBILITY);
  const [columnOrder, setColumnOrder] = useState<ColumnId[]>([...COLUMN_IDS]);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState<{ key: ColumnId | null; direction: 'asc' | 'desc' }>({
    key: 'date',
    direction: 'desc'
  });

  // Load preferences
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const prefs = JSON.parse(saved);
        if (prefs.visible) setVisibleColumns(prefs.visible);
        if (prefs.order) setColumnOrder(prefs.order);
      } catch (e) {
        console.error("Failed to load table preferences", e);
      }
    }
  }, []);

  // Save preferences
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      visible: visibleColumns,
      order: columnOrder
    }));
  }, [visibleColumns, columnOrder]);

  const handleSort = (id: ColumnId) => {
    setSortConfig(prev => ({
      key: id,
      direction: prev.key === id && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedOrders = useMemo(() => {
    if (!sortConfig.key) return orders;

    return [...orders].sort((a, b) => {
      const key = sortConfig.key!;
      let valA: any;
      let valB: any;

      switch (key) {
        case 'date':
          valA = new Date(a.orderDate).getTime();
          valB = new Date(b.orderDate).getTime();
          break;
        case 'ref':
          valA = a.id;
          valB = b.id;
          break;
        case 'supplier':
          valA = a.supplier;
          valB = b.supplier;
          break;
        case 'consignee':
          valA = a.customerName;
          valB = b.customerName;
          break;
        case 'grade':
          valA = a.items?.[0]?.name || '';
          valB = b.items?.[0]?.name || '';
          break;
        case 'quantity':
          valA = a.items?.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0) || 0;
          valB = b.items?.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0) || 0;
          break;
        case 'rate':
          valA = a.items?.[0]?.unitPrice || 0;
          valB = b.items?.[0]?.unitPrice || 0;
          break;
        case 'port':
          valA = a.portOfDestination;
          valB = b.portOfDestination;
          break;
        case 'status':
          valA = a.currentStatus;
          valB = b.currentStatus;
          break;
        default:
          return 0;
      }

      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [orders, sortConfig]);

  const toggleColumn = (id: ColumnId) => {
    setVisibleColumns(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const moveColumn = (index: number, direction: 'up' | 'down') => {
    const newOrder = [...columnOrder];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;
    
    [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
    setColumnOrder(newOrder);
  };

  const resetToDefault = () => {
    setVisibleColumns(DEFAULT_VISIBILITY);
    setColumnOrder([...COLUMN_IDS]);
    setSortConfig({ key: 'date', direction: 'desc' });
  };

  const renderHeader = (id: ColumnId) => {
    if (!visibleColumns[id]) return null;
    const isNumeric = ['quantity', 'rate'].includes(id);
    const isCenter = id === 'ref';
    const isSorted = sortConfig.key === id;

    return (
      <th 
        key={id} 
        scope="col" 
        className={`px-6 py-5 whitespace-nowrap cursor-pointer hover:bg-slate-100 transition-colors select-none group/th ${isNumeric ? 'text-right' : isCenter ? 'text-center' : 'text-left'}`}
        onClick={() => handleSort(id)}
      >
        <div className={`flex items-center gap-2 ${isNumeric ? 'justify-end' : isCenter ? 'justify-center' : 'justify-start'}`}>
          <span className={isSorted ? 'text-indigo-600' : 'text-slate-500'}>
            {COLUMN_LABELS[id]}
          </span>
          <div className="flex-shrink-0">
            {isSorted ? (
              sortConfig.direction === 'asc' ? <ArrowUp size={14} className="text-indigo-600" /> : <ArrowDown size={14} className="text-indigo-600" />
            ) : (
              <ArrowUpDown size={14} className="text-slate-300 opacity-0 group-hover/th:opacity-100 transition-opacity" />
            )}
          </div>
        </div>
      </th>
    );
  };

  const renderCell = (id: ColumnId, order: Order) => {
    if (!visibleColumns[id]) return null;
    
    const hasMultipleItems = order.items && order.items.length > 1;
    const totalQuantity = order.items?.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0) || 0;
    const primaryItem = order.items?.[0];

    switch (id) {
      case 'date': return <td key={id} className="px-6 py-6 whitespace-nowrap text-slate-500 font-medium">{order.orderDate}</td>;
      case 'ref': return (
        <td key={id} className="px-6 py-6 whitespace-nowrap font-bold text-indigo-600 text-center">
          {order.id.split('-').pop()}
        </td>
      );
      case 'supplier': return <td key={id} className="px-6 py-6 whitespace-nowrap text-slate-900 font-semibold uppercase">{order.supplier}</td>;
      case 'consignee': return <td key={id} className="px-6 py-6 whitespace-nowrap text-slate-700 font-bold uppercase truncate max-w-[200px]">{order.customerName}</td>;
      case 'grade': return (
        <td key={id} className="px-6 py-6 whitespace-nowrap">
          <div className="flex flex-col gap-0.5 max-w-[240px]">
            {order.items && order.items.length > 0 ? (
                order.items.map((item, idx) => (
                    <span 
                        key={item.id || idx} 
                        className="text-slate-700 font-bold uppercase truncate"
                    >
                        {item.name || 'N/A'}
                    </span>
                ))
            ) : (
                <span className="text-slate-300 italic text-sm">No grades assigned</span>
            )}
          </div>
        </td>
      );
      case 'quantity': return (
        <td key={id} className="px-6 py-6 whitespace-nowrap text-right font-black text-slate-900">
          {totalQuantity.toLocaleString()} MT
        </td>
      );
      case 'rate': return (
        <td key={id} className="px-6 py-6 whitespace-nowrap text-right font-semibold text-slate-600">
          <div className="flex flex-col items-end">
             <span>${primaryItem?.unitPrice?.toLocaleString() || '0'}</span>
             {hasMultipleItems && <span className="text-[8px] font-black text-indigo-400 uppercase tracking-tighter mt-0.5">Variable Rates</span>}
          </div>
        </td>
      );
      case 'port': return (
        <td key={id} className="px-6 py-6 whitespace-nowrap">
          <div className="flex items-center gap-2 text-slate-700 font-bold text-xs uppercase">
            <Anchor size={14} className="text-indigo-400 shrink-0" />
            <span className="truncate max-w-[150px]">{order.portOfDestination || 'TBD'}</span>
          </div>
        </td>
      );
      case 'status': return (
        <td key={id} className="px-6 py-6 whitespace-nowrap text-center">
          <StatusBadge status={order.currentStatus} />
        </td>
      );
      default: return <td key={id}></td>;
    }
  };

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-slate-200 flex flex-col relative overflow-hidden transition-all duration-300">
      <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-20">
        <div className="text-sm font-bold text-slate-700 flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
              <Package size={16} />
            </div>
            <span>Pipeline Registry ({sortedOrders.length} Entries)</span>
        </div>
        
        <div className="relative">
            <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`flex items-center space-x-2 text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-2xl border transition-all active:scale-95 ${isMenuOpen ? 'bg-indigo-600 text-white border-indigo-600 shadow-xl shadow-indigo-100' : 'text-slate-600 bg-white hover:bg-slate-50 border-slate-200 shadow-sm'}`}
            >
                <Columns size={14} />
                <span>Registry Layout</span>
                <ChevronDown size={14} className={`transition-transform duration-300 ${isMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {isMenuOpen && (
                <>
                <div className="fixed inset-0 z-10" onClick={() => setIsMenuOpen(false)}></div>
                <div className="absolute right-0 mt-3 w-80 bg-white rounded-[32px] shadow-2xl border border-slate-100 z-20 py-3 animate-in fade-in slide-in-from-top-4 overflow-hidden ring-1 ring-black ring-opacity-5">
                    <div className="px-6 py-4 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Display Preferences</span>
                        <button 
                            onClick={resetToDefault}
                            className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 uppercase flex items-center gap-1.5 transition-colors"
                        >
                            <RefreshCcw size={12} /> Reset
                        </button>
                    </div>
                    <div className="max-h-[450px] overflow-y-auto py-2 custom-scrollbar">
                        {columnOrder.map((id, index) => {
                            const isVisible = visibleColumns[id];
                            return (
                                <div
                                  key={id}
                                  className={`w-full px-4 py-2 flex items-center justify-between transition-all duration-200 group ${isVisible ? 'bg-indigo-50/10' : 'opacity-60'}`}
                                >
                                  <button
                                    onClick={() => toggleColumn(id)}
                                    className="flex items-center flex-1 text-left"
                                  >
                                    <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center mr-3 transition-all ${isVisible ? 'bg-indigo-600 border-indigo-600 shadow-md' : 'border-slate-200 bg-white'}`}>
                                        {isVisible && <Check size={12} className="text-white" strokeWidth={4} />}
                                    </div>
                                    <span className={`text-sm font-semibold ${isVisible ? 'text-indigo-700' : 'text-slate-500'}`}>{COLUMN_LABELS[id]}</span>
                                  </button>
                                  
                                  <div className="flex items-center gap-1">
                                    <button 
                                      disabled={index === 0}
                                      onClick={() => moveColumn(index, 'up')}
                                      className="p-1 text-slate-300 hover:text-indigo-600 disabled:opacity-0 transition-all"
                                    >
                                      <ArrowUp size={14} />
                                    </button>
                                    <button 
                                      disabled={index === columnOrder.length - 1}
                                      onClick={() => moveColumn(index, 'down')}
                                      className="p-1 text-slate-300 hover:text-indigo-600 disabled:opacity-0 transition-all"
                                    >
                                      <ArrowDown size={14} />
                                    </button>
                                  </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                </>
            )}
        </div>
      </div>

      <div className="overflow-x-auto custom-scrollbar">
        <table className="min-w-full divide-y divide-slate-100">
          <thead className="bg-slate-50/80 backdrop-blur-sm">
            <tr className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
              {columnOrder.map(renderHeader)}
              <th scope="col" className="px-8 py-5 text-right sticky right-0 bg-slate-50 z-10">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {sortedOrders.map((order) => (
              <tr 
                key={order.id} 
                onClick={() => onView(order)}
                className="hover:bg-indigo-50/10 transition-colors cursor-pointer group text-sm"
              >
                {columnOrder.map(id => renderCell(id, order))}

                <td className="px-8 py-6 whitespace-nowrap text-right sticky right-0 bg-white group-hover:bg-slate-50 transition-colors z-10 shadow-[-4px_0_12px_-4px_rgba(0,0,0,0.05)]">
                  <div className="flex items-center justify-end space-x-1">
                    <button 
                      onClick={(e) => { e.stopPropagation(); onView(order); }}
                      className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-white hover:shadow-sm rounded-xl transition-all"
                    >
                      <Eye size={18} />
                    </button>
                    {canEdit && (
                        <button 
                        onClick={(e) => { e.stopPropagation(); onEdit?.(order); }}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-white hover:shadow-sm rounded-xl transition-all"
                        >
                        <Edit2 size={18} />
                        </button>
                    )}
                    {canDelete && (
                        <button 
                        onClick={(e) => { e.stopPropagation(); onDelete?.(order.id); }}
                        className="p-2 text-slate-400 hover:text-rose-600 hover:bg-white rounded-xl shadow-sm transition-all"
                        >
                        <Trash2 size={18} />
                        </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sortedOrders.length === 0 && (
          <div className="py-32 flex flex-col items-center justify-center text-center px-6 bg-white">
              <div className="w-20 h-20 bg-slate-50 rounded-[32px] flex items-center justify-center text-slate-200 mb-6">
                <Package size={40} />
              </div>
              <h3 className="text-xl font-bold text-slate-800 uppercase tracking-tighter">Registry Empty</h3>
              <p className="text-slate-400 text-sm mt-2 max-w-xs leading-relaxed font-medium">No logistical records match the active criteria.</p>
          </div>
      )}
    </div>
  );
};